/**
 * Hook for handling concurrent match scenarios and edge cases
 */

import { useCreateChat } from "@features/messaging/queries";
import { concurrentMatchService } from "@features/messaging/services/concurrentMatchService";
import logger from "@logger";
import { useCallback, useRef } from "react";

/**
 * Hook for handling concurrent match scenarios
 */
export function useConcurrentMatchHandling() {
  const createChatMutation = useCreateChat();

  // Track recent requests to prevent spam
  const recentRequests = useRef<Map<string, number>>(new Map());

  const handleConcurrentLikes = useCallback(async (userId1: string, userId2: string) => {
    try {
      logger.info("Handling concurrent likes", { userId1, userId2 });

      const requestKey = `${userId1}-${userId2}`;
      const now = Date.now();
      const lastRequest = recentRequests.current.get(requestKey);

      if (lastRequest && now - lastRequest < 1000) {
        logger.warn("Rapid successive request detected", { userId1, userId2 });
        return {
          isMatch: false,
          isDuplicate: true,
          error: "Please wait before liking again",
        };
      }

      recentRequests.current.set(requestKey, now);

      const validation = await concurrentMatchService.validateMatchRequest(userId1, userId2);
      if (!validation.isValid) {
        logger.warn("Invalid match request", { userId1, userId2, error: validation.error });
        return {
          isMatch: false,
          error: validation.error,
          isDuplicate: false,
        };
      }

      const result = await concurrentMatchService.handleConcurrentLikes(userId1, userId2);

      if (result.isMatch && result.chatId) {
        logger.info("Concurrent match handled successfully", {
          userId1,
          userId2,
          chatId: result.chatId,
        });
      }

      return result;
    } catch (error) {
      logger.error("Error handling concurrent likes:", error);
      return {
        isMatch: false,
        error: error instanceof Error ? error.message : "Unknown error",
        isDuplicate: false,
      };
    }
  }, []);

  const handleRapidLikes = useCallback(async (userId1: string, userId2: string) => {
    try {
      logger.info("Handling rapid likes", { userId1, userId2 });

      const result = await concurrentMatchService.handleRapidLikes(userId1, userId2, Date.now());
      return result;
    } catch (error) {
      logger.error("Error handling rapid likes:", error);
      return {
        isMatch: false,
        error: error instanceof Error ? error.message : "Unknown error",
        isDuplicate: false,
      };
    }
  }, []);

  const handleNetworkFailure = useCallback(async (userId1: string, userId2: string) => {
    try {
      logger.info("Handling network failure recovery", { userId1, userId2 });

      const result = await concurrentMatchService.handleNetworkFailure(userId1, userId2);
      return result;
    } catch (error) {
      logger.error("Error handling network failure:", error);
      return {
        isMatch: false,
        error: error instanceof Error ? error.message : "Unknown error",
        isDuplicate: false,
      };
    }
  }, []);

  const createChatWithRetry = useCallback(
    async (userId1: string, userId2: string, maxRetries: number = 3): Promise<string> => {
      let retries = 0;
      let lastError: Error | unknown;

      while (retries < maxRetries) {
        try {
          const result = await createChatMutation.mutateAsync({
            userId1,
            userId2,
          });

          logger.info("Chat created successfully with retry", {
            userId1,
            userId2,
            chatId: result,
            retries,
          });

          return result;
        } catch (error) {
          lastError = error;
          retries++;
          logger.warn("Chat creation failed, retrying", {
            userId1,
            userId2,
            retries,
            error,
          });

          if (retries >= maxRetries) {
            throw error;
          }

          // Wait before retrying
          await new Promise((resolve) => setTimeout(resolve, 1000 * retries));
        }
      }

      // This should never be reached, but TypeScript needs it
      throw lastError || new Error("Chat creation failed after all retries");
    },
    [createChatMutation]
  );

  /**
   * Handle edge case of duplicate match creation
   */
  const handleDuplicateMatch = useCallback(
    async (userId1: string, userId2: string) => {
      try {
        logger.info("Handling duplicate match", { userId1, userId2 });

        // Check if chat already exists
        const existingChat = await concurrentMatchService.findExistingChat(userId1, userId2);
        if (existingChat) {
          logger.info("Duplicate match detected, chat already exists", {
            userId1,
            userId2,
            chatId: existingChat.id,
          });

          return {
            isMatch: true,
            chatId: existingChat.id,
            isDuplicate: true,
          };
        }

        // No duplicate, proceed normally
        return await handleConcurrentLikes(userId1, userId2);
      } catch (error) {
        logger.error("Error handling duplicate match:", error);
        return {
          isMatch: false,
          error: error instanceof Error ? error.message : "Unknown error",
          isDuplicate: false,
        };
      }
    },
    [handleConcurrentLikes]
  );

  /**
   * Clear recent requests (for cleanup)
   */
  const clearRecentRequests = useCallback(() => {
    recentRequests.current.clear();
  }, []);

  return {
    handleConcurrentLikes,
    handleRapidLikes,
    handleNetworkFailure,
    createChatWithRetry,
    handleDuplicateMatch,
    clearRecentRequests,
    isCreatingChat: createChatMutation.isPending,
    createChatError: createChatMutation.error,
  };
}
